// Home redirect, workspace creation, and the board list.
import { TRASH_DAYS, type BoardInfo, type MeResponse, type TrashedBoard, type WorkspaceInfo } from '../../shared/api.ts';
import { ApiError, del, get, post } from '../api.ts';
import { openTemplatePicker } from './template-picker.ts';
import { h } from '../util/dom.ts';
import {
  busy, centered, daysLeft, field, formError, getMe, header, lastWorkspace, longDate, mount, notice, relativeTime, rememberWorkspace, setTitle, welcomePage,
} from './page.ts';
import { landingPage } from './landing-page.ts';
import { claimedLocalBoards, deleteLocalBoard, getLocalBoard, localBoardsSoon, type LocalBoardInfo } from '../local/boards.ts';

const MOVE_DISMISSED_KEY = 'local-move-dismissed';

/** Signed out: the start page. Signed in: your workspace. */
export async function homePage(): Promise<void> {
  const me = await get<MeResponse>('/api/me', { redirectOn401: false }).catch((err: unknown) => {
    if ((err as ApiError).status === 401) return null;
    throw err;
  });
  if (!me) return landingPage();
  if (!me.user.name) return welcomePage();
  if (!me.workspaces.length) return createWorkspacePage(me);
  const last = lastWorkspace();
  const ws = me.workspaces.find((w) => w.slug === last) ?? me.workspaces[0]!;
  location.replace(`/w/${ws.slug}`);
}

export async function createWorkspacePage(me?: MeResponse): Promise<void> {
  me ??= await getMe();
  setTitle('New workspace');
  const name = h('input', { type: 'text', required: '', maxlength: '80', placeholder: 'e.g. Acme Design' });
  const submit = h('button', { type: 'submit', class: 'btn primary' }, ['Create workspace']);
  const error = formError();
  const form = h('form', { class: 'stack' }, [field('Workspace name', name), error, submit]);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    error.clear();
    void busy(submit, async () => {
      try {
        const ws = await post<WorkspaceInfo>('/api/workspaces', { name: name.value });
        location.assign(`/w/${ws.slug}`);
      } catch (err) {
        error.show(err);
      }
    });
  });
  const intro = me.workspaces.length
    ? 'A workspace is a separate space for a team: its own boards and members.'
    : "You're not in any workspace yet. Create one, or ask a teammate to invite you to theirs.";
  mount(header(me), centered(h('h1', {}, ['Create a workspace']), h('p', { class: 'muted' }, [intro]), form));
  name.focus();
}

export async function workspacePage(slug: string): Promise<void> {
  const me = await getMe();
  const ws = me.workspaces.find((w) => w.slug === slug);
  if (!ws) throw new ApiError(404, 'Workspace not found.');
  rememberWorkspace(ws.slug);
  setTitle(ws.name);
  // Guests never manage boards, so they have no trash (spec §16.4).
  const [boards, trash] = await Promise.all([
    get<BoardInfo[]>(`/api/workspaces/${ws.id}/boards`),
    ws.role === 'guest' ? Promise.resolve([] as TrashedBoard[]) : get<TrashedBoard[]>(`/api/workspaces/${ws.id}/trash`),
  ]);
  let trashed = trash.length;
  const trashLink = h('a', { class: 'nav-link trash-link', href: `/w/${ws.slug}/trash` });
  const showTrash = () => {
    trashLink.textContent = `Trash (${trashed})`;
    trashLink.hidden = !trashed;
  };
  showTrash();

  const actions: Node[] = [trashLink];
  // As the server decides (spec §14.1): members on Team; only owners on Free; nobody while read-only.
  const canCreate = ws.role !== 'guest' && (ws.plan === 'team' ? ws.billingStatus !== 'lapsed' : ws.role === 'owner');
  if (canCreate) {
    const create = h('button', { type: 'button', class: 'btn primary' }, ['New board']);
    create.addEventListener('click', () => openTemplatePicker(ws));
    actions.push(create);
  }

  /** The user's own workspace, for copying boards out of one they can't edit. */
  const home = me.workspaces.find((w) => w.role === 'owner' && w.id !== ws.id) ?? null;
  const grid = h('ul', { class: 'board-grid', role: 'list' });
  for (const b of boards) grid.append(boardCard(b));
  const empty = h('div', { class: 'empty' }, [
    h('p', {}, [ws.role === 'guest' ? 'No boards have been shared with you yet.' : 'No boards yet. Create the first one.']),
  ]);

  // Boards kept in this browser: with the page if IndexedDB answers quickly, else added when it does (never waited on).
  const localSlot = h('div');
  const showLocal = (local: LocalBoardInfo[]) => { if (local.length) localSlot.replaceChildren(moveLocalPrompt(ws, local)); };
  if (ws.role !== 'guest') {
    const local = await localBoardsSoon(300, showLocal);
    if (local) showLocal(local);
  }
  const plan = planNotice(ws);
  mount(
    header(me, ws, 'boards'),
    h('main', { class: 'page' }, [
      ...(plan ? [plan] : []),
      localSlot,
      h('div', { class: 'page-head' }, [h('h1', {}, ['Boards']), h('div', { class: 'page-actions' }, actions)]),
      boards.length ? grid : empty,
    ]),
  );

  /**
   * Boards made in this browser before signing in (spec §14.4): offer to move
   * them in. Each local copy is deleted only once its import has succeeded.
   */
  function moveLocalPrompt(target: WorkspaceInfo, boards: LocalBoardInfo[]): HTMLElement {
    const n = boards.length;
    const move = h('button', { type: 'button', class: 'btn small primary' }, [`Move ${n === 1 ? 'it' : 'them'} into ${target.name}`]);
    const later = h('button', { type: 'button', class: 'btn small subtle' }, ['Not now']);
    const error = formError();
    const box = h('section', { class: 'move-local', 'aria-label': 'Boards in this browser' }, [
      h('p', {}, [h('strong', {}, [`${n} ${n === 1 ? 'board is' : 'boards are'} saved only in this browser.`]),
        ` Move ${n === 1 ? 'it' : 'them'} into ${target.name} to keep ${n === 1 ? 'it' : 'them'} safe and share ${n === 1 ? 'it' : 'them'}.`]),
      error,
      h('div', { class: 'move-local-actions' }, [move, later]),
    ]);
    try { if (sessionStorage.getItem(MOVE_DISMISSED_KEY)) box.hidden = true; } catch { /* show it */ }
    later.addEventListener('click', () => {
      box.hidden = true;
      try { sessionStorage.setItem(MOVE_DISMISSED_KEY, '1'); } catch { /* ignore */ }
    });
    move.addEventListener('click', () => void busy(move, async () => {
      error.clear();
      const open = await claimedLocalBoards();
      const skipped: string[] = [];
      try {
        for (const info of boards) {
          if (open.has(info.id)) {
            skipped.push(info.title);
            continue;
          }
          const b = await getLocalBoard(info.id);
          if (!b) continue;
          await post(`/api/workspaces/${target.id}/boards/import`, { title: b.title || 'Untitled board', objects: b.objects.filter((o) => o.type !== 'image' && o.type !== 'audio') });
          await deleteLocalBoard(b.id);
        }
      } catch (err) {
        error.show(err);
        return;
      }
      if (skipped.length) {
        error.show(new Error(`Close the tab where ${skipped.map((t) => `"${t}"`).join(', ')} ${skipped.length === 1 ? 'is' : 'are'} open, then try again.`));
        return;
      }
      location.reload();
    }));
    return box;
  }

  /** Where the plan stands, when it matters here (spec §14.1). */
  function planNotice(w: WorkspaceInfo): HTMLElement | null {
    const owner = w.role === 'owner';
    const planLink = (label: string, primary = true) => h('a', { class: primary ? 'btn small primary' : 'btn small', href: `/w/${w.slug}/plan` }, [label]);
    if (w.plan === 'team' && w.billingStatus === 'lapsed') {
      return notice([h('strong', {}, ['This workspace is read-only.']), ' Its Team plan has ended; everyone can still view and export its boards, and nothing has been deleted.',
        ...(owner ? [] : [' You can copy boards you created into your own workspace.'])], owner ? [planLink('Choose what happens next')] : [], 'warn');
    }
    if (owner && w.billingStatus === 'past_due') {
      return notice([h('strong', {}, ['Payment failed.']), " Update your card to keep Team. Everyone can keep working for now."], [planLink('Update card')], 'warn');
    }
    if (owner && w.billingStatus === 'trialing' && w.trialEndsAt) {
      const days = daysLeft(w.trialEndsAt);
      return notice([h('strong', {}, [`Team trial: ${days} ${days === 1 ? 'day' : 'days'} left.`]), ` Subscribe before ${longDate(w.trialEndsAt)} to keep editing together.`],
        [planLink('See plans', days <= 3)]);
    }
    if (w.plan === 'free' && !owner && w.role !== 'guest') {
      return notice(['This workspace is on the Free plan, where only its owner edits. You can view its boards.']);
    }
    return null;
  }

  function boardCard(b: BoardInfo): HTMLElement {
    const meta = `Edited ${relativeTime(b.updatedAt)}${b.visibility === 'private' ? ' · Private' : ''}${b.canEdit ? '' : ' · View only'}`;
    const item = h('li', { class: 'board-card' }, [
      h('a', { href: `/b/${b.id}`, class: 'board-link' }, [
        h('div', { class: 'board-thumb', 'aria-hidden': 'true' }, b.thumbAt
          ? [h('img', { src: `/api/boards/${b.id}/thumbnail?v=${b.thumbAt}`, alt: '', loading: 'lazy', decoding: 'async' })]
          : []),
        h('div', { class: 'board-meta' }, [h('strong', {}, [b.title]), h('span', { class: 'muted' }, [meta + (b.publicToken ? ' · Published' : '')])]),
      ]),
    ]);
    if (b.canManage) {
      const remove = h('button', { type: 'button', class: 'card-action', 'aria-label': `Delete ${b.title}`, title: 'Delete board' }, ['Delete']);
      remove.addEventListener('click', () => {
        if (!confirm(`Move "${b.title}" to the trash? It can be restored for ${TRASH_DAYS} days.`)) return;
        void busy(remove, async () => {
          await del(`/api/boards/${b.id}`);
          item.remove();
          if (!grid.children.length) grid.replaceWith(empty);
          trashed++;
          showTrash();
        }).catch((err: unknown) => alert((err as Error).message));
      });
      item.append(remove);
    }
    // Stuck in a workspace you can't edit (read-only, or Free and not yours): take your own boards with you.
    if (!b.canEdit && b.createdBy === me.user.id && home) {
      const copy = h('button', { type: 'button', class: 'card-action copy', title: `Copy to ${home.name}` }, ['Copy to my workspace']);
      copy.addEventListener('click', () => void busy(copy, async () => {
        try {
          const made = await post<BoardInfo>(`/api/boards/${b.id}/copy`, { workspaceId: home.id });
          copy.replaceWith(h('a', { class: 'card-action copy done', href: `/b/${made.id}` }, [`Copied: open in ${home.name}`]));
        } catch (err) {
          alert((err as Error).message);
        }
      }));
      item.append(copy);
    }
    return item;
  }
}
